import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import { Box, Grid, Button, Tab, Tabs, Typography, useTheme, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {checkDamageTypeState, checkOutInModeState, checkOutInStepState, checkOutInBufferDataActionTypeState, eventsTypeState, checkOutInPartsAndPositionsState, checkOutInSeverityState,
	existingDamageState, raFetchedDataState, checkOutInEventsListState, checkOutInBufferDataActionState, checkVehicleStatusState, extraCostInputState} from "../state";
import DamageList from "./damage-list/DamageList";
import CheckStepper from "../check-stepper/CheckStepper";
import {IDamage, IDamageOperation, IEvent, IEventCard} from "../../models/models";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";
import { deepClone } from "../../../../util/state/commonFunctions";
import { checkInOutScenario } from "../../types"
import {fetchCheckOutInAddAttachments, fetchCheckOutInDeleteAttachmentBlogById, fetchPartsAndPositions, fetchSeverity,
	fetchCheckOutInAddSnapshot, fetchCheckOutInAssignDamageToForm, fetchCheckOutInCreateDamage, fetchCheckOutInCreateForm,
	fetchCheckOutInDamagesByPlate, fetchCheckOutInDeleteDamage, fetchCheckOutInDeleteForm,
	fetchCheckOutInUpdateCounterpartyData, fetchEventsList, 
	uploadVehicleAdjustmentPics} from "../services/http.services";
import { EVENT_ATTACHMENT_TYPE, EVENT_MODULES_CODE } from "../config";
import axios, {AxiosError} from "axios";
import {getInsertionFlow} from "../services/utils";
import AddPhotos, { TFilePayload } from "../add-photos/AddPhotos";
import {resizeImg} from "../../utils";
import ExtraCostsBox from "../extra-costs/ExtraCostsBox";
import {
	centsToEuroInput,
	EMPTY_EXTRA_COSTS,
	ExtraCostAttachment,
	ExtraCostDocument,
	ExtraCostKind,
	ExtraCostsValue,
	euroInputToCents,
	isExtraCostDocument
} from "../extra-costs/extraCosts";

const getPhotosValidationUrl = (raId: string, scenario: string) => `/network/adjust/${scenario}?idRentalAgreement=${encodeURIComponent(raId)}`

const buildExtraCostDocument = async (
	attachment: ExtraCostAttachment | null,
	raId: string,
	kind: ExtraCostKind
): Promise<ExtraCostDocument | null> => {
	if (!attachment || isExtraCostDocument(attachment)) {
		return attachment;
	}

	const extension = attachment.name.split('.').pop() ?? 'jpg';
	const documentTitle = `${kind === 'wash' ? 'extra_wash' : 'extra_recovery'}.${extension}`;
	const resizedFile = await resizeImg(attachment);

	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(resizedFile as Blob);
		reader.onload = () => resolve({
			documentFile: String(reader.result ?? ''),
			path: `${raId}/checkIn`,
			contentType: extension,
			documentTitle,
			fileSize: attachment.size
		});
		reader.onerror = () => reject(reader.error);
	});
};

const CheckDamage = () => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);

	const extDmgListRef = useRef<any>();

	const { dispatchGlobalData, globalData } = useContext(GlobalContext);

	const [activeDamageScheme, setActiveDamageScheme] = useState('ext');
	const [activeTabExDm, setActiveTabExDm] = useState(0);
	const [activeTabIntDm, setActiveTabIntDm] = useState(0);

	const [checkStep, setCheckStep] = useRecoilState<any>(checkOutInStepState);
	const checkVehicle = useRecoilValue(checkVehicleStatusState);
	const [severityList, setSeverityList] = useRecoilState(checkOutInSeverityState);
	const [partsAndPositionsList, setPartsAndPositionsList] = useRecoilState(checkOutInPartsAndPositionsState);
	const [eventsList, setEventsList] = useRecoilState(checkOutInEventsListState);

	// fetch from BE
	const raFetchedData = useRecoilValue(raFetchedDataState);
	// modalità attiva
	const checkOutInMode = useRecoilValue(checkOutInModeState);

	/** Existing Damages */
	const [existingDamages, setExistingDamages] = useRecoilState(existingDamageState);

	/** NEW Damages */
	const [checkDamageInternal, setCheckDamageInternal] = useRecoilState<any>(checkDamageTypeState('internal'));
	const [checkDamageExternal, setCheckDamageExternal] = useRecoilState<any>(checkDamageTypeState('external'));
	const [eventsInternal, setEventsInternal] = useRecoilState<IEvent[]>(eventsTypeState('internal'));
	const [eventsExternal, setEventsExternal] = useRecoilState<IEvent[]>(eventsTypeState('external'));
	// clone Obj
	const checkDamageInt = deepClone(checkDamageInternal);
	const checkDamageExt = deepClone(checkDamageExternal);
	const eventsInt = deepClone(eventsInternal);
	const eventsExt = deepClone(eventsExternal);

	const [isOpenPhotoDialog, setIsOpenPhotoDialog] = useState(false);
	const [waitingForAxios, setWaitingForAxios] = useState(false);
	const [checkinBlocked, setCheckinBlocked] = useState(false);

	// LPP-22962 (RDT-LPP-22291 §3 SCFE-1): due box extra costi manuali (lavaggio/recupero) al check-in.
	// Allegato obbligatorio quando l'importo è valorizzato (AS-09); l'invio al BE proxy (LPP-22963) e la
	// persistenza sul RA avvengono in fase di finalize.
	const [extraCostInputValues, setExtraCostInputValues] = useRecoilState<any>(extraCostInputState);
	const [extraCosts, setExtraCosts] = useState<ExtraCostsValue>(() => ({
		wash: {
			amount: euroInputToCents(extraCostInputValues?.washExtra),
			description: extraCostInputValues?.washDescription ?? '',
			attachment: extraCostInputValues?.washPhoto ?? null
		},
		recovery: {
			amount: euroInputToCents(extraCostInputValues?.recoveryExtra),
			description: extraCostInputValues?.recoveryDescription ?? '',
			attachment: extraCostInputValues?.recoveryPhoto ?? null
		}
	}));
	const [extraCostsValid, setExtraCostsValid] = useState<boolean>(true);
	const isCheckInMode = checkOutInMode === 'in' || checkOutInMode === 'subIn';
	// LPP-25233: veicolo non marciante (es. furto) -> sezioni del check-in bloccate, costi extra compresi:
	// box disabilitato e nessun importo inviato, neanche quelli rimasti in sessione.
	const extraCostsDisabled = !!checkVehicle?.nonRunningVehicle;
	const effectiveExtraCosts = extraCostsDisabled ? EMPTY_EXTRA_COSTS : extraCosts;

	// calc tot new damage num
	let extNewTotalDamages = checkDamageExt.length;
	eventsExt.map((event: IEvent) => extNewTotalDamages += event.damages.length);
	let intNewTotalDamages = checkDamageInt.length;
	eventsInt.map((event: IEvent) => intNewTotalDamages += event.damages.length);

	// Buffer Data Action
	const [bufferDataAction, setDataBufferAction] = useRecoilState(checkOutInBufferDataActionState);
	const [bufferDataActionEvent, setBufferDataActionEvent] = useRecoilState(checkOutInBufferDataActionTypeState('event'));
	const setBufferDataActionDamage = useSetRecoilState(checkOutInBufferDataActionTypeState('damage'));

	const enablePhotos = useMemo<boolean>(() => {
		const tenants: string[] =
			globalData.user.legalEntities
				.filter((elem: any) => elem.name !== 'all')
				.map((elem: any) => elem.name);
		//console.log('LB raFetchedData', raFetchedData);
		const isRA = !!raFetchedData?.idRentalAgreement;
		const enable = isRA && 
               (tenants.includes('drivalia_fr') || tenants.includes('drivalia_francia')) && 
               (raFetchedData?.idTenant === 'drivalia_fr' || raFetchedData?.idTenant === 'drivalia_francia');

		return enable;
	}, [globalData, raFetchedData]);

	useEffect(() => {
		(async () => {
			if (enablePhotos && (checkOutInMode === 'in' || checkOutInMode === 'subIn')) {
				setWaitingForAxios(true);
				const adjustmentProcessValidationResult = await axios(getPhotosValidationUrl(raFetchedData.idRentalAgreement, checkInOutScenario.CheckOut))
					.then((res: any) => {
						return res.data
					})
					.catch((err: any) => {
						console.error(err);
					});

				if (!adjustmentProcessValidationResult?.adjustmentDone) {
					setCheckinBlocked(true);
					dispatchGlobalData({
						type: 'SET_SNACKBAR',
						payload: {
							snackbar: {
								message: t('raCheckOutIn.error.photosMissingCheckout'),
								type: snackbarTypes.error,
								show: true,
								autoClose: true,
							}
						}
					});
				}

				setWaitingForAxios(false);
			}
		})();
	}, [checkOutInMode, enablePhotos])

	const handleChangeDamageType = (e: React.SyntheticEvent, activeTab: number) => {
		e.stopPropagation();

		if (activeDamageScheme === 'ext') {
			setActiveTabExDm(activeTab);
		} else {
			setActiveTabIntDm(activeTab);
		}
	}

	const handleChangeDamageScheme = (e: React.SyntheticEvent, activeScheme: 'int' | 'ext') => {
		e.stopPropagation();

		if (activeScheme === 'ext') {
			setActiveDamageScheme(activeScheme);
		} else {
			setActiveDamageScheme(activeScheme);
		}
	}

	const handleContinue = async (e: React.SyntheticEvent) => {
		e.stopPropagation();

		const eventsFormValues: any[] = extDmgListRef?.current?.getFormValue();
		const validationOutcome = await extDmgListRef?.current?.checkFormValidation();
		if (validationOutcome === false) {
			return;
		}

		if (isCheckInMode) {
			const [washPhoto, recoveryPhoto] = await Promise.all([
				buildExtraCostDocument(effectiveExtraCosts.wash.attachment, raFetchedData.idRentalAgreement, 'wash'),
				buildExtraCostDocument(effectiveExtraCosts.recovery.attachment, raFetchedData.idRentalAgreement, 'recovery')
			]);
			setExtraCostInputValues((current: any) => ({
				...current,
				washExtra: centsToEuroInput(effectiveExtraCosts.wash.amount),
				washDescription: effectiveExtraCosts.wash.description,
				washPhoto,
				recoveryExtra: centsToEuroInput(effectiveExtraCosts.recovery.amount),
				recoveryDescription: effectiveExtraCosts.recovery.description,
				recoveryPhoto
			}));
		}

		if (enablePhotos) {
			const scenario = ({
				"out": checkInOutScenario.CheckOut,
				"in": checkInOutScenario.CheckIn,
				"subIn": checkInOutScenario.CheckIn,
			} as { [k: string]: string })[checkOutInMode];

			setWaitingForAxios(true);
			const adjustmentProcessValidationResult = await axios(getPhotosValidationUrl(raFetchedData.idRentalAgreement, scenario))
				.then((res: any) => res.data)
				.catch((err: any) => {
					console.error(err);
					return null;
				});
			console.dir({ adjustmentProcessValidationResult })
			setWaitingForAxios(false);
	
			if (!adjustmentProcessValidationResult?.adjustmentDone) {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('raCheckOutIn.error.photosMissing'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
				return;
			}
		}

		// effettuare il test senza aggiungere danni e clickare su tab dei danni
		if (!!eventsFormValues) {
            for(const [ind, event] of eventsExt.entries()) {
                const eventInd = eventsFormValues?.findIndex((evt: IEventCard) => evt.eventId === event.id) ?? -1;
                if (eventInd !== -1) {
                    eventsExt[ind].plate = eventsFormValues[eventInd].counterpartData.plate;
                    eventsExt[ind].date = eventsFormValues[eventInd].counterpartData.date;
                    eventsExt[ind].place = eventsFormValues[eventInd].counterpartData.place;
                }
            }
        }

		if (checkDamageInternal.length > 0 || checkDamageExternal.length > 0) {
			dispatchGlobalData({
				type: 'SET_SNACKBAR',
				payload: {
					snackbar: {
						message: t('raCheckOutIn.error.freeDamage'),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					}
				}
			});
			return;
		}

		// delete empty events
		// const intExtEvents = eventsExt.concat(eventsInt); // TODO: con introduzione dei eventi interni eliminare eventi vuoti anche li
		const emptyEvent = eventsExt.filter((event: IEvent) => event.damages.length === 0);
		emptyEvent.map((event: any) =>
			deleteEmptyFormMutation.mutate(event.id)
		);

		const fillUpEvent = eventsExt.filter((event: IEvent) => event.damages.length !== 0);
		setEventsExternal(fillUpEvent);

		setDataBufferAction({
			damage: {
				action: null,
				data: null
			},
			event: {
				action: null,
				data: null
			},
			equipments: {
				action: null,
				previousCheckData: null
			}
		});

		const counterPartyList: any[] = [];
		fillUpEvent.forEach((event: IEvent) => counterPartyList.push({
			damageFormId: event.id,
			counterpartyPlate: event.plate,
			eventDate: event.date,
			eventCity: event.place
		}));

		updateCounterpartyDataMutation.mutate({counterPartyList: counterPartyList} as any);
	}

	const viewDefaultError = (message = 'translation:error.tryLater', type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(message),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	/** Queries */
	const vehicleDamagesQuery = useQuery(
		['vehicleDamages', {tenant: raFetchedData.idTenant, plate: raFetchedData.vehicle.plate.code}],
		() => fetchCheckOutInDamagesByPlate({
			tenant: raFetchedData.idTenant,
			plate: raFetchedData.vehicle.plate.code,
			insertionFlow: getInsertionFlow(checkOutInMode),
			raCode: raFetchedData.idRentalAgreement
		}),
		{
			enabled: false,
			onSuccess: (response: {existingClosedDamage: IDamageOperation[], newOpenedDamage: any[], notAssignedDamages: IDamageOperation[], attachmentsIdList: any}) => {
				if (!!response) {
					// botch BE error response
					if (typeof response === "string" && (response as string).includes("Error")) { 		// error
						viewDefaultError('raCheckOutIn.checkDamage.message.error.getDamages');
						return;
					}

					setExistingDamages({
						visited: true,
						internal: [],
						external: response.existingClosedDamage
					});

					// new open damages - events non closed
					const events =
						response.newOpenedDamage
							.map((elem: any): IEvent => {
								const cid = elem.attachmentsIdList?.filter((attach: any) => attach.documentType === EVENT_MODULES_CODE.cai);
								const den = elem.attachmentsIdList?.filter((attach: any) => attach.documentType === EVENT_MODULES_CODE.den);

								return {
									id: elem.id,
									eventName: elem.event,
									counterpart: !!elem.counterpart ? elem.counterpart : false,
									claimForm: !!den?.[0] ? {id: den[0].id} : null,
									cid: !!cid?.[0] ? {id: cid[0].id } : null,
									damages: elem.damages
								}
							})

					setEventsExternal(events);

					// damage without form
					setCheckDamageExternal(response.notAssignedDamages);
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Create Open*/
	const createDamageMutation = useMutation(
		fetchCheckOutInCreateDamage,
		{
			onSuccess: (response: any) => {
				if (typeof response === "string") { 		// error
					viewDefaultError();
				}
				else { 										// update damage id
					if (!response?.[0]) {
						viewDefaultError();
					} else {
						const dmg = deepClone(bufferDataAction.damage.data);
						dmg.id = response[0].id;
						checkDamageExt.push(dmg);
						setCheckDamageExternal(checkDamageExt);
					}
				}
			},
			onError: (err: AxiosError) => {
				let mess = 'translation:error.tryLater';

				// @ts-ignore
				if (err.response?.status === 400 && err.response?.data?.errorDetails === "\"ERROR_DAMAGE_ALREADY_EXISTS\"") {
					mess = "common.error.errorDamageAlreadyExist"
				}

				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t(mess),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
				}
			});

	/** Delete  Damage */
	const deleteDamageMutation = useMutation(
		fetchCheckOutInDeleteDamage,
		{
			onSuccess: (response: any) => {
				if (!!response && response.includes("Error")) { // error
					viewDefaultError();
				}
				else {
					const ind = checkDamageExt.findIndex((elem: any) => elem.id === bufferDataAction.damage.data.id );
					if (ind !== -1) {
						checkDamageExt.splice(ind, 1);
						setCheckDamageExternal(checkDamageExt);
					}
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Add Photo */
	const addSnapshotMutation = useMutation(
		fetchCheckOutInAddSnapshot,
		{
			onSuccess: (response: any) => {
				let damage = deepClone(bufferDataAction.damage.data.dmg);
				const ind = checkDamageExt.findIndex((elem: any) => elem.id === damage.id );
				if (ind !== -1) {
					damage.snapshotIdList.push(response[0].id);
					checkDamageExt.splice(ind, 1, damage);
				}

				setCheckDamageExternal(checkDamageExt);
			},
			onError: (error) => {
				viewDefaultError();
				setBufferDataActionDamage(null);
			}
		});

	/** Create Form */
	const createFormMutation = useMutation(
		fetchCheckOutInCreateForm,
		{
			onSuccess: (response: any) => {
				if (typeof response === "string") { // error
					viewDefaultError('raCheckOutIn.checkDamage.message.error.createForm');
				}
				else { // associate damages to created form
					if (!response?.[0]) {
						viewDefaultError();
					} else {
						const evt = deepClone(bufferDataAction.event.data);
						evt.id = response[0].id;
						setBufferDataActionEvent({
							action: 'associate',
							data: evt
						});

						const evtExt = deepClone(evt);
						evtExt.damages = [];
						eventsExt.push(evtExt);
						setEventsExternal(eventsExt);
					}
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Delete Form */
	const deleteFormMutation = useMutation(
		fetchCheckOutInDeleteForm,
		{
			onSuccess: (response: any) => {// response as string: 'Delete...:ID_NUMBER' || 'Error...'
				if (!!response && response.includes("Error")) { // error
					viewDefaultError();
				}
				else { // delete damages to created form
					const ind = eventsExt.findIndex((evt: any) => evt.id === bufferDataActionEvent.data.id);
					const evtDmgs =
						eventsExt[ind].damages
							.map((elem: IDamage) => {
								elem.damageFormId = null;
								return elem;
							});
					eventsExt.splice(ind, 1);
					checkDamageExt.push(...evtDmgs);
					setCheckDamageExternal(checkDamageExt);
					setEventsExternal(eventsExt);
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Delete Empty Form */
	const deleteEmptyFormMutation = useMutation(
		fetchCheckOutInDeleteForm,
		{
			onSuccess: (response: any) => { },
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** UpdateCounterpart Data */
	const updateCounterpartyDataMutation = useMutation(
		fetchCheckOutInUpdateCounterpartyData,
		{
			onSuccess: (response: any) => {
				setCheckStep({
					active: checkStep.active + 1,
					numSteps: checkStep.numSteps
				});
			},
			onError: (error) => {
				viewDefaultError();
			}
		});

	/** Assign Damage To  Form */
	const assignDamageToFormMutation = useMutation(
		fetchCheckOutInAssignDamageToForm,
		{
			onSuccess: (response: any) => {
				if (!response?.[0]) {
					viewDefaultError();
				} else {
					// get selected event
					const ind = eventsExt.findIndex((evt: any) => evt.id === response[0].formId);
					if (ind === -1) {
						viewDefaultError();
					}
					else {
						if (eventsExt[ind].damages.length < response[0].damageIdList.length) { // associate new Damage
							// exclude damg from free damage list
							const dmgExt =
								checkDamageExt
									.filter((dmg: IDamage) => !response[0].damageIdList.includes(dmg.id));
							setCheckDamageExternal(dmgExt);

							// add dmg to event's damage list
							const associatedDmgs =
								checkDamageExt
									.filter((dmg: IDamage) => response[0].damageIdList.includes(dmg.id))
									.map((dmg: IDamage) => {
										dmg.damageFormId = response[0].formId;
										return dmg;
									})

							eventsExt[ind].damages = eventsExt[ind].damages.concat(associatedDmgs);

							setEventsExternal(eventsExt);
						}
						else { // disassociate from event case
							const dmgExt =
								checkDamageExt
									.concat(
										eventsExt[ind].damages
											.filter((dmg: IDamage) => !response[0].damageIdList.includes(dmg.id))
											.map((dmg: IDamage) =>	{
												dmg.damageFormId = null;
												return dmg;
											})
									);
							setCheckDamageExternal(dmgExt);

							eventsExt[ind].damages =
								eventsExt[ind].damages
									.filter((dmg: IDamage) => response[0].damageIdList.includes(dmg.id));
							setEventsExternal(eventsExt);
						}
					}
				}
			},
			onError: (error: AxiosError) => {
			    let mess = '';
			    let messageType = snackbarTypes.info;
			    // @ts-ignore
                if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"OPR_KEY_PLATE_CONFLICT\"") {
                    mess = "common.error.oprConflictKeyAndPlate";
                    messageType = snackbarTypes.info;
                }
                // @ts-ignore
                else if (error.response?.status === 500 && error.response?.data?.errorDetails === "\"OPR_GENERIC_ERROR\"") {
                    mess = "common.error.oprGenericError"
                    messageType = snackbarTypes.error;
                }
                else{
                    mess = 'translation:error.tryLater';
                    messageType = snackbarTypes.error;
                }
                viewDefaultError(mess, messageType);
			}
		});

	/** Upload event's modules */
	const addEventAttachmentsMutation = useMutation(
		fetchCheckOutInAddAttachments,
		{
			onSuccess: (response: any) => {
				if (!response?.[0]) {
					viewDefaultError();
				} else {
					// get selected event
					const ind = eventsExt.findIndex((evt: any) => evt.id === response[0].damageFormId);
					if (ind === -1) {
						viewDefaultError();
					}
					else {
						switch (response[0].documentType) {
							case EVENT_MODULES_CODE.cai:
								eventsExt[ind].cid = response[0];
								break;
							case EVENT_MODULES_CODE.den:
								eventsExt[ind].claimForm = response[0];
								break;
							default:
								viewDefaultError();
								return;
						}

						setEventsExternal(eventsExt);
					}
				}
			},
			onError: (error) => {
				setEventsExternal(eventsExt);
				viewDefaultError();
			}
		});

	const getSeverityList = useQuery(
		['fetchSeverity'],
		() =>  fetchSeverity(),
		{
			enabled: false,
			onSuccess: (response: any) => {
				if (!!response) {
					//TODO Aggiungere logica severity e fixare parametri per endpoint
					setSeverityList(response);
				}
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('raCheckOutIn.error.getSeverity'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		})

	const eventsListQuery = useQuery(
		['eventsListQuery'],
		() => fetchEventsList({tenant: raFetchedData.idTenant}),
		{
			enabled: false,
			onSuccess: (response: any) => {
				if (!!response) {
					//TODO Aggiungere logica severity e fixare parametri per endpoint
					setEventsList(response);
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		})

	const getPartsAndPositionsList = useQuery(
		[' fetchPartsAndPositions', {tenant: raFetchedData.idTenant}],
		() =>  fetchPartsAndPositions({tenant: raFetchedData.idTenant}),
		{
			enabled: false,
			onSuccess: (response: any) => {
				if (!!response) {
					//TODO Aggiungere logica parti e posizioni (elenco completo) e fixare parametri per endpoint
					setPartsAndPositionsList(response);
				}
			},
			onError: (error) => {
				viewDefaultError();
			}
		})

	const fetchCheckOutInDeleteAttachmentBlogByIdMutation = useMutation(
		fetchCheckOutInDeleteAttachmentBlogById,
		{
			onSuccess: (response: any) => {
				if (response === "Error while deleting attachment") {
					dispatchGlobalData({
						type: 'SET_SNACKBAR',
						payload: {
							snackbar: {
								message: t('raCheckOutIn.checkDamage.message.error.deleteEventAttach'),
								type: snackbarTypes.error,
								show: true,
								autoClose: true,
							}
						}
					});
				} else {
					const outcome = response.split(':');
					const id = parseInt(outcome[1]);
					for (const [index, event] of eventsExt.entries()) {
						if (event.cid?.id === id) {
							eventsExt[index].cid = null;
							break;
						} else if (event.claimForm?.id === id) {
							eventsExt[index].claimForm = null;
							break;
						}
					}
					setEventsExternal(eventsExt);
				}
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	/** Damage Action */
	useEffect(() => {
		if (!bufferDataAction?.damage?.action) {
			return;
		}

		switch (bufferDataAction.damage.action) {
			case 'create':
				// @ts-ignore
				createDamageMutation.mutate({
					tenant:  raFetchedData.idTenant,
					open: {
						vehicleVin: 		raFetchedData.vehicle.vin,
						vehiclePlate: 		raFetchedData.vehicle.plate.code,
						damageList: [{
							partId: 		bufferDataAction.damage.data.part.id,
							positionId: 	bufferDataAction.damage.data.position.id,
							severity:		bufferDataAction.damage.data.severity,
							figureCode: 	bufferDataAction.damage.data.figureCode,
						}]
					}
				});
				break;
			case 'delete':
				// @ts-ignore
				deleteDamageMutation.mutate(bufferDataAction.damage.data.id);
				break;
			case 'addSnapshot':
				// upload bin file
				const form = new FormData();
				form.append('snapshots', bufferDataAction.damage.data.file);

				addSnapshotMutation.mutate({
					idRentalAgreement: encodeURIComponent(raFetchedData.idRentalAgreement),
					damageId: bufferDataAction.damage.data.dmg.id,
					damageIsOpen: true,
					snapshots: form
				} as any);
				break;
			case 'delSnapshot':
				// check checkDamageExternal
				for (const [i, dmg] of checkDamageExt.entries()) {
					for (const [y,snapshot] of dmg.snapshotIdList.entries()) {
						if (snapshot === bufferDataAction.damage.data) {
							checkDamageExt[i].snapshotIdList.splice(y, 1);
							setCheckDamageExternal(checkDamageExt);
							return;
						}
					}
				}

				// check checkDamageInternal
				for (const [i, dmg] of checkDamageInt.entries()) {
					for (const [y,snapshot] of dmg.snapshotIdList.entries()) {
						if (snapshot === bufferDataAction.damage.data) {
							checkDamageInt[i].snapshotIdList.splice(y, 1);
							setCheckDamageInternal(checkDamageInt);
							return;
						}
					}
				}

				// check event damages
				for (const [i, event] of eventsExt.entries()) {
					for (const [y, dmg] of event.damages.entries()) {
						for (const [z, snapshot] of dmg.snapshotIdList.entries()) {
							if (snapshot === bufferDataAction.damage.data) {
								eventsExt[i].damages[y].snapshotIdList.splice(z, 1);
								setEventsExternal(eventsExt);
								return;
							}
						}
					}
				}

				// check event damages
				for (const [i, event] of eventsInt.entries()) {
					for (const [y, dmg] of event.damages.entries()) {
						for (const [z, snapshot] of dmg.snapshotIdList.entries()) {
							if (snapshot === bufferDataAction.damage.data) {
								eventsInt[i].damages[y].snapshotIdList.splice(z, 1);
								setEventsInternal(eventsInt);
								return;
							}
						}
					}
				}
				break;
		}
	},[bufferDataAction.damage]);

	/** Event Action */
	useEffect(() => {
		if (!bufferDataAction.event.action) {
			return;
		}

		switch (bufferDataAction.event.action) {
			case 'create':
				// @ts-ignore
				createFormMutation.mutate({
					tenant: raFetchedData.idTenant,
					form: {
						"vin": 			raFetchedData.vehicle?.vin,
						"plate": 		raFetchedData.vehicle?.plate?.code,
						"raOwner":		raFetchedData.raOwner?.firstName + ' ' + raFetchedData.raOwner?.lastName,
						"raSectional": 	"Sectional",
						"raId": 		raFetchedData.idRentalAgreement,
						"formList":[{
							"counterparty": checkOutInMode === 'out' ? false : bufferDataAction.event?.data?.counterpart,
							"eventId": 		bufferDataAction.event.data?.eventName?.id
						}],
						insertionFlow: getInsertionFlow(checkOutInMode)
					}
				});
				break;
			case 'associate':
				if (!!bufferDataActionEvent.data.id) {
					const damageIdList =
						bufferDataActionEvent.data.damages
							.map((dmg: IDamage) => dmg.id);

					// @ts-ignore
					assignDamageToFormMutation.mutate({
						tenant: raFetchedData.idTenant,
						damages: [{
							"damageIdList": damageIdList,
							"formId": bufferDataActionEvent.data.id
						}]
					});
				} else {
					viewDefaultError(); // il danno non può essere associato perche evento non è stato creato/selezionato
				}
				break;
			case 'addAttachments': // add modules -> 'CAI' | 'DICHIARAZIONE_CLIENTE'
				// upload bin file
				const form = new FormData();
				form.append('attachments', bufferDataAction.event.data.module);

				addEventAttachmentsMutation.mutate({
					damageFormId:  		bufferDataAction.event?.data?.eventId,
					attachmentTypeId: 	EVENT_ATTACHMENT_TYPE.id,
					documentType: 		bufferDataAction.event?.data?.documentType,
					snapshots: 			form
				} as any);

				break;
			case 'delete':
				deleteFormMutation.mutate(bufferDataAction.event.data.id);
				break;
			case 'deleteEventAttach':
				fetchCheckOutInDeleteAttachmentBlogByIdMutation.mutate({
					idAttachment: bufferDataAction.event.data.id,
					tenant: raFetchedData.idTenant,
					type: "ATTACHMENT"} as any);
				break;
		}

	},[bufferDataAction.event]);

	useEffect(() => {
		if (!eventsList || eventsList.length === 0) {
			eventsListQuery.refetch();
		}
		if (!partsAndPositionsList || partsAndPositionsList.length === 0) {
			getPartsAndPositionsList.refetch();
		}
		if (!severityList || severityList.length === 0) {
			getSeverityList.refetch();
		}
		if (!existingDamages.visited) {
			vehicleDamagesQuery.refetch();
		}
	},[]);

	const uploadAdjustmentPicturesMutation = useMutation(
		uploadVehicleAdjustmentPics,
		{
			onSuccess: (response: any) => {
				if (!!response && response.message !== "OK") { // error
					console.error(response.message);
					viewDefaultError();
				}
				else {
					setIsOpenPhotoDialog(false);
				}
			},
			onError: (error) => {
				console.error(error);
				viewDefaultError();
			}
		});

	function submitAdjustmentPictures(images: TFilePayload[]) {
		const rentalAgreementId = raFetchedData.idRentalAgreement;
		const vehicleId = raFetchedData.vehicle.plate.id; // TODO: What is the vehicleId ???
		const tenant = raFetchedData.idTenant;
		const dealer = ""; // TODO: Who is the dealer ???

		// TODO: Multiple possible values, like subIn
		const scenario = ({
			"out": checkInOutScenario.CheckOut,
			"in": checkInOutScenario.CheckIn,
			"subIn": checkInOutScenario.CheckIn,
		} as { [k: string]: string })[checkOutInMode];

		uploadAdjustmentPicturesMutation.mutate({
			rentalAgreementId,
			vehicleId,
			tenant,
			dealer,
			scenario,
			images,
		})
	}

	return (
		<Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
			<Grid container columnSpacing={2}>
				<Grid item xs={12} sx={{display: "flex", justifyContent: "center", alignContent: "space-between", mb: theme.spacing(2)}}>
					{/*{ activeDamageScheme === 'int' &&*/}
					{/*	<>*/}
                    {/*        <Button  sx={{mx: theme.spacing(1)}} startIcon={<CheckCircleOutlineRounded sx={{color: theme.palette.neutral.light}} />} >*/}
					{/*			{t("Interno")}*/}
                    {/*        </Button>*/}

					{/*		<Button variant={"text"} sx={{mx: theme.spacing(1)}} onClick={(e: React.SyntheticEvent) => handleChangeDamageScheme(e, 'ext')}>*/}
					{/*			{t("Esterno")}*/}
                    {/*        </Button>*/}
					{/*	</>*/}
					{/*}*/}

					{/*{ activeDamageScheme === 'ext' &&*/}
					{/*	<>*/}
                    {/*        <Button variant={"text"} sx={{mx: theme.spacing(1)}} onClick={(e: React.SyntheticEvent) => handleChangeDamageScheme(e, 'int')}>*/}
					{/*			{t("Interno")}*/}
                    {/*        </Button>*/}
                    {/*        <Button  sx={{mx: theme.spacing(1)}} startIcon={<CheckCircleOutlineRounded sx={{color: theme.palette.neutral.light}} />} >*/}
					{/*			{t("Esterno")}*/}
                    {/*        </Button>*/}
					{/*	</>*/}
					{/*}*/}

				</Grid>

				<Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
					<Typography variant={"h6"} >
						{t("raCheckOutIn.checkDamage.header.title")}
					</Typography>
				</Grid>

				{/* external damage */}
				{
					activeDamageScheme === 'ext' &&
					<>
                        <Grid item xs={12} md={6}>
                            <img src={"/images/network/vehicle-damage-scheme.png"} className="width-100 height-auto"/>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box>
                                <Tabs value={activeTabExDm} onChange={handleChangeDamageType} >
                                    <Tab label={t("raCheckOutIn.checkDamage.tab.existingDamages")}/>
                                    <Tab label={t("raCheckOutIn.checkDamage.tab.newlyDetectedDamage") + ' ' + extNewTotalDamages}/>
                                </Tabs>
                            </Box>

							{/* Existing damage */}
							{activeTabExDm === 0 &&
								<Box role={'tabpanel'} sx={{pl: theme.spacing(1)}}>
									<DamageList damages={existingDamages.external} readOnly={true}/>
								</Box>
							}

							{/* New damage */}
							{activeTabExDm === 1 &&
								<Box role={'tabpanel'} sx={{pl: theme.spacing(1)}}>
									<DamageList damages={checkDamageExt} eventList={eventsExt} ref={extDmgListRef} />
								</Box>
							}

							{ enablePhotos &&
								<Box sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
									<Button color={"accent"} disabled={checkinBlocked} onClick={() => setIsOpenPhotoDialog(true)}>{t('raCheckOutIn.checkDamage.button.addStatusPictures')}</Button>
								</Box>
							}
                        </Grid>
					</>
				}

				{/*/!* Internal damage *!/*/}
				{/*{*/}
				{/*	activeDamageScheme === 'int' &&*/}
                {/*    <>*/}
                {/*        <Grid item xs={12} md={6}>*/}
                {/*            /!*<img src={"/images/network/vehicle-damage-internal.JPG"} className="width-100 height-auto"/>*!/*/}
                {/*        </Grid>*/}

                {/*        <Grid item xs={12} md={6}>*/}
                {/*            <Box>*/}
                {/*                <Tabs value={activeTabIntDm} onChange={handleChangeDamageType} >*/}
                {/*                    <Tab label={t("Danni esistenti")}/>*/}
                {/*                    <Tab label={t(`Danni nuovi rilevati (${intNewTotalDamages})`)}/>*/}
                {/*                </Tabs>*/}
                {/*            </Box>*/}

				{/*			/!* Existing damage *!/*/}
				{/*			{activeTabIntDm === 0 &&*/}
				{/*				<Box role={'tabpanel'} sx={{pl: theme.spacing(1)}}>*/}
				{/*					<DamageList damages={existingDamages.internal} readOnly={true}/>*/}
				{/*				</Box>*/}
				{/*			}*/}

				{/*			/!* New damage *!/*/}
				{/*			{activeTabIntDm === 1 &&*/}
				{/*				<Box role={'tabpanel'} sx={{pl: theme.spacing(1)}}>*/}
				{/*					<DamageList damages={checkDamageInt} eventList={eventsInt} vehicleParts={vehicleParts} updateDmList={updateDmList}/>*/}
				{/*				</Box>*/}
				{/*			}*/}

                {/*        </Grid>*/}
                {/*    </>*/}
				{/*}*/}
			</Grid>

			{ isCheckInMode &&
				<ExtraCostsBox
					value={effectiveExtraCosts}
					disabled={extraCostsDisabled}
					onChange={(value, isValid) => { setExtraCosts(value); setExtraCostsValid(isValid); }}
				/>
			}

			<CheckStepper />

			<Box sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
				<Button color={"accent"} disabled={!extraCostsValid && !extraCostsDisabled} onClick={handleContinue}>{t('raCheckOutIn.checkDamage.button.continue')}</Button>
			</Box>

			{
				enablePhotos &&
				<AddPhotos open={isOpenPhotoDialog} onClose={() => setIsOpenPhotoDialog(false)} onSubmit={submitAdjustmentPictures}></AddPhotos>
			}

			<CustomLoader loading={vehicleDamagesQuery.isFetching || addEventAttachmentsMutation.isLoading || fetchCheckOutInDeleteAttachmentBlogByIdMutation.isLoading ||
									eventsListQuery.isFetching || getSeverityList.isFetching || getPartsAndPositionsList.isFetching ||
									createDamageMutation.isLoading || deleteDamageMutation.isLoading || addSnapshotMutation.isLoading ||
									createFormMutation.isLoading || deleteFormMutation.isLoading || assignDamageToFormMutation.isLoading ||
									uploadAdjustmentPicturesMutation.isLoading || waitingForAxios
			} />
		</Paper>
	);
}

export default CheckDamage;
